import { Alert, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { useEffect, useRef } from 'react'
import { FC, useState } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { useEventValidation } from '../../contexts/EventValidationContext'

interface EditorProps {
  setEditorText: any
  editorText: any
  validationField?: string
  placeholder?: string
  style?: React.CSSProperties
  maxLength?: number
  disabled?: boolean
}

export const Editor: FC<EditorProps> = ({ editorText, setEditorText, validationField, placeholder = 'Type here', style, maxLength, disabled }) => {
  const [dirtyText, setDirtyText] = useState(editorText)
  const [textLength, setTextLength] = useState(0)
  const [isDirty, setIsDirty] = useState(false)
  const [error, setError] = useState("")
  const quillRef = useRef<ReactQuill>(null)
  const debug = false
  
  // Always call the hook, but only use it if validationField is provided
  const validationContext = useEventValidation()

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [
        { list: 'bullet' },
        { list: 'ordered' },
        { indent: '-1' },
        { indent: '+1' }
      ],
      [{ 'link': true }],
      [{ header: [false, 2, 3, 4, 5, 6] }]
    ]
  }

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
    'unlink',
    'image'
  ]

  const quill = quillRef?.current?.getEditor()

  useEffect(() => {
    if (dirtyText !== editorText) {
      setIsDirty(true)
    }
    setTextLength(quill?.getLength() || 0)
  }, [dirtyText])

  useEffect(() => {
    setDirtyText(editorText)
    setTextLength(quill?.getLength() || 0)
  }, [editorText])

  const handleBlur = () => {
    if (dirtyText !== editorText) {
      setEditorText(dirtyText)
      setIsDirty(false)
    }
  }

  const handleChange = (content: string) => {
    const newTextLength = quill?.getLength() || 0
    if (maxLength) {
      if (newTextLength <= maxLength) {
        setError("")
        // Clear error in validation context
        if (validationField) {
          validationContext.clearFieldError(validationField)
        }
      } else {
        const errorMsg = "You have exceeded the maximum length of " + maxLength + " characters."
        setError(errorMsg)
        // Set error in validation context
        if (validationField) {
          validationContext.setFieldError(validationField, errorMsg)
        }
      }
    }
    setDirtyText(content)
    setEditorText(content)
  }

  return (
    <div>
      <style>
        {`
          .ql-editor[contenteditable="false"] {
            background-color: #f5f5f5;
            color: #666;
            cursor: not-allowed;
          }
          .ql-container.ql-disabled .ql-editor {
            background-color: #f5f5f5;
            color: #666;
            cursor: not-allowed;
          }
          .ql-container.ql-disabled {
            background-color: #f5f5f5;
          }
        `}
      </style>
      {error && <Alert severity="error">{error}</Alert>}
      {debug && <Box>
          <pre>{JSON.stringify(dirtyText, null, 2)}</pre>
        </Box>}
      <Box>
        <ReactQuill
          ref={quillRef}
          theme='snow'
          modules={modules}
          formats={formats}
          value={dirtyText}
          placeholder={placeholder}
          onChange={handleChange}
          onBlur={handleBlur}
          style={{ ...style }}
          readOnly={disabled}
        />
        {maxLength && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Typography variant='caption'>
              {textLength} / {maxLength}
            </Typography>
          </Box>
        )}
      </Box>
    </div>
  )
}
